import { v4 as uuid } from 'uuid';
import { toBrazilianDate } from '../../../../helpers/utils/utils';
import { formatBRL } from '../../UI';

/**
 * Mapper puro DTO real (`findAllRecalculosProcessoService`) → shape de UI —
 * replica `makeListaRecalculos` real (`ListaRecalculoProcesso.jsx`,
 * AW2-1093 Etapa 0), campo a campo. O DTO real não tem nenhum id próprio de
 * registro — a tela real também gera uma chave client-side (`uuid().slice(0,6)`,
 * `key`) para usar como `key` do React; aqui reaproveitamos o mesmo padrão
 * (`uuid()` completo, já usado no protótipo em `historicoController.js`).
 *
 * `isVazio`/`formatPercentual`/`formatMoeda` seguem o mesmo cuidado
 * null-safe já documentado em `AbaFinanceira.jsx` (`isVazio`) — nunca
 * `Number('') === 0` mascarando ausência de dado como zero genuíno. Só os
 * campos de valor/percentual passam por essa checagem — os demais campos de
 * texto usam `?? ''` (mesmo padrão do resto do módulo).
 */
function isVazio(valor) {
  return valor === null || valor === undefined || valor === '';
}

function formatPercentual(valor) {
  return isVazio(valor) ? '' : `${Number(valor).toFixed(2)}%`;
}

function formatMoeda(valor) {
  return isVazio(valor) ? '' : formatBRL(valor);
}

/**
 * Colunas visíveis da grid (AC2) — table-driven, header e corpo iteram
 * sobre o MESMO array (mesmo padrão já usado em `TableTitulosProcesso.jsx`,
 * nunca declarar o corpo como lista JSX hardcoded em paralelo ao header).
 * Rótulo da última coluna ("Saldo Valor Principal") confirmado por leitura
 * direta de `TableHeaderListaRecalculoProcesso.jsx` real — divergia do mock
 * anterior do protótipo ("Valor Recalculado").
 */
export const COLUNAS = [
  { campo: 'dataRecalculo', label: 'Data do Recálculo', align: 'center' },
  { campo: 'dataBaseRecalculo', label: 'Data Base', align: 'center' },
  { campo: 'nomeFase', label: 'Fase', align: 'left' },
  { campo: 'nomeUsuario', label: 'Nome do Responsável', align: 'left' },
  { campo: 'motivoRecalculo', label: 'Motivo do Recálculo', align: 'left' },
  { campo: 'descricaoTipoDesconto', label: 'Tipo de Desconto', align: 'left' },
  { campo: 'valorTotal', label: 'Saldo Valor Principal', align: 'right' },
];

function mapearRecalculo(dto) {
  return {
    id: uuid(),
    dataRecalculo: toBrazilianDate(dto?.dataRecalculo) || '',
    dataBaseRecalculo: toBrazilianDate(dto?.dataBaseRecalculo) || '',
    nomeFase: dto?.nomeFase ?? '',
    nomeUsuario: dto?.nomeUsuario ?? '',
    motivoRecalculo: dto?.motivoRecalculo ?? '',
    descricaoTipoDesconto: dto?.descricaoTipoDesconto ?? '',
    valorTotal: formatMoeda(dto?.valorTotal),
    porcentagemTaxaJuros: formatPercentual(dto?.porcentagemTaxaJuros),
    valorJuros: formatMoeda(dto?.valorJuros),
    utilizarJurosSimples: dto?.utilizarJurosSimples,
    calcularJurosUnico: dto?.calcularJurosUnico,
    porcentagemHonorarios: formatPercentual(dto?.porcentagemHonorarios),
    valorHonorarios: formatMoeda(dto?.valorHonorarios),
    porcentagemDesconto: formatPercentual(dto?.porcentagemDesconto),
    valorDesconto: formatMoeda(dto?.valorDesconto),
    porcentagemTaxaCartao: formatPercentual(dto?.porcentagemTaxaCartao),
    valorTaxaCartao: formatMoeda(dto?.valorTaxaCartao),
  };
}

export function mapearRecalculos(content) {
  return (content || []).map(mapearRecalculo);
}
